// «حجز جديد» (board 27 reservation--new, HANDOFF §17 + §1 side panel 420):
// «هاتف أو حاضر أو شركة · القنوات الخارجية تصل وحدها». النزيل by phone or
// name from the ERP's customer book (an existing guest «نزيل سابق · ٣», or a
// new one: name + phone); الوصول / المغادرة («الأحد ١٨ · ٣ ليالٍ» — the
// nights are the quote's); the type cards from GET /hotel/quote (free count,
// «متاح / آخر الغرف / ممتلئ», the plan's first-night price + «$46 · السعر
// العام», and the corporate plan's price from its own quote); الضيوف;
// المصدر; الخطة chips (the quote's plans); العربون (the quote's figure, the
// rule text only when the payload carries the rule); the total line. Writes:
// POST /hotel/reservations — «حفظ كمبدئي» (tentative, no deposit, R3) and
// the coral «تأكيد الحجز». A refusal (409/422) shows the server's detail
// verbatim and keeps the panel open; one write at a time.
//
// Rule 7: every figure is the quote's. The client never prices, counts or
// sums — not even nights × price (the total line prints the quote's
// `nights` and `total_minor`; «× price» only when the server's own figures
// agree, as a guard). A type the plan does not price (null) is said so in
// words; a type it prices at 0 (a comp stay) is bookable.

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, ChevronDown, Minus, Plus, Search, X } from 'lucide-react';
import { Button, Field, PrimaryAction, SidePanel, Skeleton, TextInput } from '@/components';
import { ApiError } from '@/lib/api';
import { normalizeIraqiPhone, formatIraqiPhone } from '@/lib/digits';
import { addDays, arNumber, formatDate, formatMoney, toArabicDigits } from '@/lib/format';
import { useDebounced } from '@/features/booking/public';
import type { CompanyItem, CreateReservationBody, DeskSource, QuoteResponse, QuoteType } from '../api';
import { CompanyPicker } from '../components/CompanyPicker';
import { HOTEL_PATHS } from '../menu';
import { useBusinessDate, useCreateReservation, useGuestSearch, useQuote } from '../data/hooks';
import { adultsLabel, childrenLabel, guestsLabel, nightsLabel, weekdayDay } from '../lib/words';
import { usdLatin } from '../lib/money';
import '../hotel.css';
import './new-reservation.css';

/** erp-core refuses a quote of more than this many nights (quote.go MaxQuoteNights). */
const MAX_NIGHTS = 31;

/** The desk sources as the panel's subtitle names them («هاتف أو حاضر أو شركة»). */
const SOURCES: ReadonlyArray<{ value: DeskSource; label: string }> = [
  { value: 'phone', label: 'هاتف' },
  { value: 'walk_in', label: 'حاضر' },
  { value: 'company', label: 'شركة' },
];

/** A guest-book row; `stays_count` is read only when the server sends it. */
interface GuestRow {
  id: string;
  name: string;
  phone: string;
  stays_count?: number;
}

/** «متاح» · «آخر الغرف» · «ممتلئ» — the availability word of a type (the server's `free` / `last_rooms`). */
export function availabilityWord(t: Pick<QuoteType, 'free' | 'last_rooms'>): { word: string; tone: 'ok' | 'warn' } {
  if (t.free <= 0) return { word: 'ممتلئ', tone: 'warn' };
  if (t.last_rooms) return { word: 'آخر الغرف', tone: 'warn' };
  return { word: 'متاح', tone: 'ok' };
}

/** «٣ متاحة» · «لا غرف متاحة». */
export function freeLabel(free: number): string {
  return free > 0 ? `${toArabicDigits(free)} متاحة` : 'لا غرف متاحة';
}

export interface NewReservationPanelProps {
  open: boolean;
  onClose: () => void;
}

export function NewReservationPanel({ open, onClose }: NewReservationPanelProps) {
  if (!open) return null;
  return <OpenNewReservation onClose={onClose} />;
}

function OpenNewReservation({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const businessDate = useBusinessDate();

  // -- guest
  const [query, setQuery] = useState('');
  const [guest, setGuest] = useState<GuestRow | null>(null);
  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const q = useDebounced(query.trim(), 250);
  const search = useGuestSearch(guest ? '' : q);
  const results = (search.data?.customers ?? []) as GuestRow[];
  const showNew = !guest && q.length > 0 && search.isSuccess && results.length === 0;

  // -- stay
  const [from, setFrom] = useState<string | null>(null);
  const [to, setTo] = useState<string | null>(null);
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [guestsOpen, setGuestsOpen] = useState(false);
  const [source, setSource] = useState<DeskSource>('phone');
  const [planId, setPlanId] = useState('rack');
  const [typeId, setTypeId] = useState<string | null>(null);
  const [company, setCompany] = useState<CompanyItem | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (businessDate && !from) {
      setFrom(businessDate);
      setTo(addDays(businessDate, 1));
    }
  }, [businessDate, from]);

  const base = from && to ? { from, to, adults, children } : null;
  const companyId = source === 'company' ? company?.customer_id : undefined;
  const quote = useQuote(base ? { ...base, plan_id: planId, unit_type_id: typeId ?? undefined, company_customer_id: companyId } : null);
  const data: QuoteResponse | undefined = quote.data;
  const current = !!data && !quote.isPlaceholderData;
  const corpPlan = data?.plans.find((p) => p.kind === 'corporate' && p.id !== planId) ?? null;
  const corpQuote = useQuote(base && corpPlan ? { ...base, plan_id: corpPlan.id, company_customer_id: companyId } : null);
  const corpByType = useMemo(() => new Map((corpQuote.data?.types ?? []).map((t) => [t.unit_type_id, t])), [corpQuote.data]);

  // A type the new dates sold out is no longer a choice.
  useEffect(() => {
    if (!current || !typeId) return;
    const t = data?.types.find((x) => x.unit_type_id === typeId);
    if (!t || t.free <= 0) setTypeId(null);
  }, [current, data, typeId]);

  const create = useCreateReservation();
  const selected = current && typeId ? data?.types.find((t) => t.unit_type_id === typeId) ?? null : null;
  // the server's answer decides: a type it prices at 0 (a comp stay) is bookable; a type it cannot price (null) is explained
  const unpriced = current && !!selected && selected.price_minor == null;
  const quoted = current && !!selected && selected.price_minor != null;

  const guestOk = guest ? true : showNew && newName.trim().length > 0 && !!normalizeIraqiPhone(newPhone);
  const canSave = !!guestOk && (source !== 'company' || !!company) && quoted && !create.isPending;

  const onQuery = (v: string) => {
    setQuery(v);
    setError(null);
    const e164 = normalizeIraqiPhone(v);
    setNewPhone(e164 ? v.trim() : '');
    setNewName(e164 ? '' : v.trim());
  };

  const changeFrom = (v: string) => {
    if (!v) return;
    setError(null);
    setFrom(v);
    if (!to || to <= v) setTo(addDays(v, 1));
    else if (to > addDays(v, MAX_NIGHTS)) setTo(addDays(v, MAX_NIGHTS));
  };
  const changeTo = (v: string) => {
    if (!v || !from) return;
    setError(null);
    setTo(v > addDays(from, MAX_NIGHTS) ? addDays(from, MAX_NIGHTS) : v);
  };

  const save = (tentative: boolean) => {
    if (!canSave || !from || !to || !typeId || !data) return;
    const body: CreateReservationBody = guest
      ? { customer_id: guest.id, customer_name: guest.name, customer_phone: guest.phone, unit_type_id: typeId, from, to, adults, children, source, rate_plan_id: data.plan_id, tentative, ...(companyId ? { company_customer_id: companyId } : {}) }
      : { new_customer: { name: newName.trim(), phone: normalizeIraqiPhone(newPhone) as string }, unit_type_id: typeId, from, to, adults, children, source, rate_plan_id: data.plan_id, tentative, ...(companyId ? { company_customer_id: companyId } : {}) };
    setError(null);
    create.mutate(body, {
      onSuccess: (r) => {
        onClose();
        navigate(`${HOTEL_PATHS.reservations}/${encodeURIComponent(r.stay_id)}`);
      },
      onError: (e) => {
        setError(e instanceof ApiError && e.detail ? e.detail : 'لم تنجح المحاولة.');
        // a 409 means the availability changed under us: read the quote again, never retry the write
        if (e instanceof ApiError && e.status === 409) {
          void quote.refetch();
          if (corpPlan) void corpQuote.refetch();
        }
      },
    });
  };

  const footer = (
    <div className="nr-footer">
      {error && (
        <div className="nr-error" role="alert">
          {error}
        </div>
      )}
      <span className="nr-note">تعيين الغرفة عند الوصول أو من اللوحة الزمنية</span>
      <div className="nr-actions">
        <Button variant="line" onClick={() => save(true)} disabled={!canSave} loading={create.isPending && create.variables?.tentative === true}>
          حفظ كمبدئي
        </Button>
        <PrimaryAction size="lg" onClick={() => save(false)} disabled={!canSave} loading={create.isPending && create.variables?.tentative === false}>
          تأكيد الحجز
        </PrimaryAction>
      </div>
    </div>
  );

  return (
    <SidePanel open title="حجز جديد" onClose={onClose} footer={footer} data-testid="new-reservation">
      <div className="nr-body">
        <p className="nr-sub">هاتف أو حاضر أو شركة · القنوات الخارجية تصل وحدها</p>

        {/* ---- النزيل ---- */}
        <Block label="النزيل">
          {guest ? (
            <div className="e-input nr-box" data-testid="res-guest">
              <span className="nr-box-text">{guest.name}</span>
              <span className="nr-chip">{guest.stays_count != null ? `نزيل سابق · ${toArabicDigits(guest.stays_count)}` : 'نزيل سابق'}</span>
              <button type="button" className="e-iconbtn nr-clear" aria-label="تغيير" onClick={() => setGuest(null)}>
                <X aria-hidden="true" />
              </button>
            </div>
          ) : (
            <>
              <TextInput icon={Search} value={query} onChange={(e) => onQuery(e.target.value)} placeholder="ابحث باسم أو رقم هاتف" aria-label="ابحث عن النزيل باسم أو رقم هاتف" data-testid="res-guest-search" />
              {results.length > 0 && (
                <ul className="nr-results" aria-label="نتائج البحث عن النزيل">
                  {results.slice(0, 6).map((c) => (
                    <li key={c.id}>
                      <button type="button" className="nr-result" onClick={() => setGuest(c)}>
                        <span className="nr-result-name">{c.name}</span>
                        <span className="ltr nr-result-sub">{formatIraqiPhone(c.phone)}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {showNew && (
                <div className="nr-newcard" data-testid="res-new-guest">
                  <span className="nr-hint">لا نتائج لـ«{q}» — أضفه نزيلاً جديداً</span>
                  <Field label="الاسم">{(id) => <TextInput id={id} value={newName} onChange={(e) => setNewName(e.target.value)} />}</Field>
                  <Field label="الهاتف">{(id) => <TextInput id={id} ltr inputMode="tel" placeholder="07XX XXX XXXX" value={newPhone} onChange={(e) => setNewPhone(e.target.value)} />}</Field>
                </div>
              )}
            </>
          )}
        </Block>

        {/* ---- الوصول / المغادرة ---- */}
        <div className="nr-row2 nr-row2--dates">
          <Block label="الوصول">
            <DateBox testId="res-from" name="الوصول" value={from} min={businessDate ?? undefined} onChange={changeFrom} text={from ? formatDate(from, { weekday: true }) : ''} />
          </Block>
          <Block label="المغادرة">
            <DateBox
              testId="res-to"
              name="المغادرة"
              value={to}
              min={from ? addDays(from, 1) : undefined}
              max={from ? addDays(from, MAX_NIGHTS) : undefined}
              onChange={changeTo}
              text={to ? (current ? `${weekdayDay(to)} · ${nightsLabel(data.nights)}` : weekdayDay(to)) : ''}
            />
          </Block>
        </div>

        {/* ---- نوع الغرفة وخطة السعر ---- */}
        <Block label="نوع الغرفة وخطة السعر">
          {quote.isError && (
            <div className="nr-error" role="alert" data-testid="res-quote-error">
              {quote.error instanceof ApiError && quote.error.detail ? quote.error.detail : 'لم تنجح المحاولة.'}{' '}
              <Button variant="line" size="sm" onClick={() => void quote.refetch()}>
                أعد المحاولة
              </Button>
            </div>
          )}
          {!quote.isError && quote.isLoading && (
            <div className="nr-types" aria-busy="true" data-testid="res-quote-loading">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="nr-type-skel">
                  <Skeleton height={16} radius={8} />
                </div>
              ))}
            </div>
          )}
          <div className="nr-types" role="radiogroup" aria-label="نوع الغرفة وخطة السعر" aria-busy={quote.isFetching || undefined} hidden={quote.isError || quote.isLoading}>
            {(data?.types ?? []).map((t) => {
              const a = availabilityWord(t);
              const full = t.free <= 0;
              const corp = corpPlan ? corpByType.get(t.unit_type_id) : undefined;
              const checked = t.unit_type_id === typeId;
              return (
                <button
                  key={t.unit_type_id}
                  type="button"
                  role="radio"
                  className="nr-type"
                  aria-checked={checked}
                  disabled={full}
                  data-full={full ? 'true' : 'false'}
                  data-name={t.name}
                  data-testid="res-type"
                  onClick={() => {
                    setError(null);
                    setTypeId(t.unit_type_id);
                  }}
                >
                  <span className="nr-type-name">
                    <span className="nr-type-title">{t.name}</span>
                    <span className="nr-type-meta">
                      <span className="nr-type-free">{freeLabel(t.free)}</span>
                      <span className="nr-type-avail" data-tone={a.tone}>
                        {a.word}
                      </span>
                    </span>
                  </span>
                  {t.price_minor != null && (
                    <span className="nr-type-price">
                      {formatMoney(t.price_minor)}
                      <span className="nr-type-sub">
                        {t.usd_cents != null && (
                          <>
                            <span dir="ltr" className="nr-usd">
                              {usdLatin(t.usd_cents)}
                            </span>
                            {' · '}
                          </>
                        )}
                        {t.plan_name}
                      </span>
                    </span>
                  )}
                  {corp && corp.price_minor != null && (
                    <span className="nr-type-corp" data-testid="res-corp">
                      {formatMoney(corp.price_minor)}
                      <span className="nr-type-sub">{corp.plan_name}</span>
                    </span>
                  )}
                  <span className="nr-radio" aria-hidden="true" />
                </button>
              );
            })}
          </div>
        </Block>

        {unpriced && (
          <p className="nr-hint" role="status" data-testid="res-no-price">
            لا سعر لهذه الخطة في هذه الليالي.
          </p>
        )}

        {/* ---- الضيوف / المصدر ---- */}
        <div className="nr-row2">
          <Block label="الضيوف">
            <button type="button" className="e-input nr-box nr-select" aria-expanded={guestsOpen} onClick={() => setGuestsOpen((o) => !o)} data-testid="res-guests">
              <span className="nr-box-text">{guestsLabel(adults, children)}</span>
              <ChevronDown aria-hidden="true" />
            </button>
            {guestsOpen && (
              <div className="nr-steppers">
                <Stepper value={adults} min={1} label={adultsLabel(adults)} less="إنقاص البالغين" more="زيادة البالغين" onChange={setAdults} />
                <Stepper value={children} min={0} label={children > 0 ? childrenLabel(children) : 'بلا أطفال'} less="إنقاص الأطفال" more="زيادة الأطفال" onChange={setChildren} />
              </div>
            )}
          </Block>
          <Block label="المصدر">
            <span className="e-input nr-box">
              <select className="nr-native" value={source} onChange={(e) => setSource(e.target.value as DeskSource)} aria-label="المصدر" data-testid="res-source">
                {SOURCES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
              <ChevronDown aria-hidden="true" />
            </span>
          </Block>
        </div>

        {source === 'company' && (
          <Block label="الشركة">
            <CompanyPicker value={company} onChange={setCompany} />
          </Block>
        )}

        {/* ---- الخطة / العربون ---- */}
        <div className="nr-stack">
          <Block label="الخطة">
            <div className="nr-plans" role="radiogroup" aria-label="الخطة">
              {(data?.plans ?? []).map((p) => (
                <button
                  key={p.id}
                  type="button"
                  role="radio"
                  aria-checked={p.id === planId}
                  className="nr-plan"
                  onClick={() => {
                    setError(null);
                    setPlanId(p.id);
                  }}
                >
                  {p.name}
                </button>
              ))}
            </div>
          </Block>
          <Block label="العربون">
            <div className="e-input nr-box" data-testid="res-deposit">
              {quoted && (
                <>
                  <span className="nr-box-text">
                    {formatMoney(data.deposit_minor)}
                    {data.deposit_pct != null && ` · ليلة واحدة ${toArabicDigits(data.deposit_pct)}٪`}
                  </span>
                  {data.deposit_pct != null && <span className="nr-tag">من الخطة</span>}
                </>
              )}
            </div>
          </Block>
        </div>

        {quoted && (
          <div className="nr-total" data-testid="res-total">
            <span className="nr-total-line">
              {/* the first night's price is drawn only when it is every night's (price × nights = the total) */}
              {selected.price_minor != null && selected.price_minor * data.nights === data.total_minor ? `${nightsLabel(data.nights)} × ${arNumber(selected.price_minor)}` : nightsLabel(data.nights)}
              {data.breakfast && ' · الإفطار مشمول'}
            </span>
            <span className="nr-total-sum">
              {formatMoney(data.total_minor)}{' '}
              {data.total_usd_cents != null && (
                <span className="nr-total-usd">
                  ≈{' '}
                  <span dir="ltr" className="nr-usd">
                    {usdLatin(data.total_usd_cents)}
                  </span>
                </span>
              )}
            </span>
          </div>
        )}
      </div>
    </SidePanel>
  );
}

function Block({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="nr-block">
      <div className="nr-label">{label}</div>
      {children}
    </div>
  );
}

function DateBox({ testId, name, value, min, max, onChange, text }: { testId: string; name: string; value: string | null; min?: string; max?: string; onChange: (v: string) => void; text: string }) {
  return (
    <label className="e-input nr-box nr-date">
      <span className="nr-box-text" data-testid={`${testId}-label`}>
        {text}
      </span>
      <Calendar aria-hidden="true" />
      <input type="date" className="nr-date-input" value={value ?? ''} min={min} max={max} onChange={(e) => onChange(e.target.value)} data-testid={testId} aria-label={name} />
    </label>
  );
}

function Stepper({ value, min, label, less, more, onChange }: { value: number; min: number; label: string; less: string; more: string; onChange: (n: number) => void }) {
  return (
    <div className="nr-stepper">
      <button type="button" className="e-iconbtn" aria-label={less} disabled={value <= min} onClick={() => onChange(Math.max(min, value - 1))}>
        <Minus aria-hidden="true" />
      </button>
      <span className="nr-stepper-value">{label}</span>
      <button type="button" className="e-iconbtn" aria-label={more} disabled={value >= 20} onClick={() => onChange(Math.min(20, value + 1))}>
        <Plus aria-hidden="true" />
      </button>
    </div>
  );
}
